import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { formatApiError } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("The two passwords do not match."); return; }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/account/reset-password`, { token, password });
      toast.success("Password updated. Please sign in.");
      nav("/account/login");
    } catch (err) {
      setError(formatApiError(err, "Could not reset your password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Set a new password — EI Battery Solutions" description="Set a new password for your EI Battery trade account." />
      <section className="max-w-md mx-auto px-4 py-16 sm:py-24" data-testid="reset-password-page">
        <h1 className="text-3xl font-bold text-ei-navy">Set a new password</h1>
        {!token ? (
          <div className="mt-6 rounded-sm border border-ei-mist bg-white p-5 text-sm text-ei-graphite" data-testid="reset-password-no-token">
            <p>This page needs the link from your reset email.</p>
            <Link to="/account/forgot-password" className="mt-3 inline-block font-semibold text-ei-navy underline" data-testid="reset-password-request-new">Request a reset link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
            <label className="block">
              <span className="text-xs font-semibold text-ei-navy">New password (at least 8 characters)</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                data-testid="reset-password-new"
                className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-ei-navy"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-ei-navy">Confirm new password</span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                data-testid="reset-password-confirm"
                className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-ei-navy"
              />
            </label>
            {error && <p className="text-sm text-red-700" data-testid="reset-password-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-password-submit"
              className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3.5 rounded-sm hover:bg-ei-ink transition-colors inline-flex items-center justify-center gap-2 min-h-[48px] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update password
            </button>
          </form>
        )}
      </section>
    </>
  );
}
